import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  api,
  type AdCreativeType,
  type CampaignPerformance,
  type CampaignStatus,
  type CampaignWithCreatives,
} from '../api';
import { AppShell } from '../components/AppShell';

export function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [campaign, setCampaign] = useState<CampaignWithCreatives | null>(null);
  const [perf, setPerf] = useState<CampaignPerformance | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Creative form
  const [adding, setAdding] = useState(false);
  const [creativeForm, setCreativeForm] = useState({
    type: 'IMAGE' as AdCreativeType,
    mediaUrl: '',
    thumbnailUrl: '',
    durationSeconds: 8,
  });

  async function load() {
    if (!id) return;
    try {
      setErr(null);
      const [c, p] = await Promise.all([
        api.getCampaign(id),
        api.campaignPerformance(id),
      ]);
      setCampaign(c);
      setPerf(p);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'failed_to_load');
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function updateStatus(status: CampaignStatus) {
    if (!id) return;
    setBusy(true);
    try {
      await api.updateCampaign(id, { status });
      await load();
    } finally {
      setBusy(false);
    }
  }

  async function addCreative(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !creativeForm.mediaUrl.trim()) return;
    try {
      await api.createCreative(id, {
        type: creativeForm.type,
        mediaUrl: creativeForm.mediaUrl.trim(),
        thumbnailUrl: creativeForm.thumbnailUrl.trim() || undefined,
        durationSeconds: Number(creativeForm.durationSeconds) || undefined,
      });
      setAdding(false);
      setCreativeForm({
        type: 'IMAGE',
        mediaUrl: '',
        thumbnailUrl: '',
        durationSeconds: 8,
      });
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'create_failed');
    }
  }

  async function removeCreative(creativeId: string) {
    if (!confirm('Delete this creative?')) return;
    await api.deleteCreative(creativeId);
    await load();
  }

  async function deleteCampaign() {
    if (!id) return;
    if (!confirm('Delete this campaign and all its data?')) return;
    await api.deleteCampaign(id);
    window.location.href = '/campaigns';
  }

  if (err) {
    return (
      <AppShell>
        <div className="card text-red-300">{err}</div>
      </AppShell>
    );
  }
  if (!campaign) {
    return (
      <AppShell>
        <div className="text-muted">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="space-y-6">
        <div>
          <Link to="/campaigns" className="text-sm text-muted hover:text-text">
            ← Campaigns
          </Link>
          <div className="mt-2 flex items-end justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">
                {campaign.advertiserName}
              </h1>
              <p className="mt-1 text-muted">
                {campaign.startDate.slice(0, 10)} →{' '}
                {campaign.endDate.slice(0, 10)} · Priority{' '}
                {campaign.priorityWeight} ·{' '}
                {campaign.impressionCap
                  ? `Cap ${campaign.impressionCap}`
                  : 'No cap'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {campaign.status !== 'ACTIVE' && (
                <button
                  className="btn btn-primary"
                  onClick={() => updateStatus('ACTIVE')}
                  disabled={busy}
                >
                  ▶ Activate
                </button>
              )}
              {campaign.status === 'ACTIVE' && (
                <button
                  className="btn btn-secondary"
                  onClick={() => updateStatus('PAUSED')}
                  disabled={busy}
                >
                  ⏸ Pause
                </button>
              )}
              <button
                className="btn btn-secondary"
                onClick={() => updateStatus('COMPLETED')}
                disabled={busy}
              >
                Complete
              </button>
              <button
                className="btn btn-ghost text-red-300"
                onClick={deleteCampaign}
                disabled={busy}
              >
                Delete
              </button>
            </div>
          </div>
        </div>

        {/* Performance summary */}
        {perf && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            <StatCard
              label="Total impressions"
              value={perf.totals.impressions.toLocaleString()}
              sub={
                perf.impressionCap
                  ? `of ${perf.impressionCap.toLocaleString()} cap`
                  : 'no cap'
              }
            />
            <StatCard
              label="Display time"
              value={formatDuration(perf.totals.durationSeconds)}
              sub="across all creatives"
            />
            <StatCard
              label="Venues reached"
              value={Object.keys(perf.byVenue).length.toString()}
              sub="distinct venues"
            />
          </div>
        )}

        {/* Creatives */}
        <div>
          <div className="mb-3 flex items-end justify-between">
            <h2 className="text-xl font-bold">Creatives</h2>
            <button
              className="btn btn-secondary"
              onClick={() => setAdding((a) => !a)}
            >
              {adding ? 'Cancel' : '+ Add creative'}
            </button>
          </div>

          {adding && (
            <form className="card mb-4 space-y-4" onSubmit={addCreative}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field label="Type">
                  <select
                    className="input"
                    value={creativeForm.type}
                    onChange={(e) =>
                      setCreativeForm({
                        ...creativeForm,
                        type: e.target.value as AdCreativeType,
                      })
                    }
                  >
                    <option value="IMAGE">Image</option>
                    <option value="VIDEO">Video</option>
                    <option value="HTML5">HTML5</option>
                    <option value="SPONSORED_OVERLAY">
                      Sponsored overlay
                    </option>
                  </select>
                </Field>
                <Field label="Duration (seconds)">
                  <input
                    className="input"
                    type="number"
                    min={1}
                    value={creativeForm.durationSeconds}
                    onChange={(e) =>
                      setCreativeForm({
                        ...creativeForm,
                        durationSeconds: Number(e.target.value),
                      })
                    }
                  />
                </Field>
                <Field label="Media URL">
                  <input
                    className="input"
                    type="url"
                    placeholder="https://…"
                    value={creativeForm.mediaUrl}
                    onChange={(e) =>
                      setCreativeForm({
                        ...creativeForm,
                        mediaUrl: e.target.value,
                      })
                    }
                    required
                  />
                </Field>
                <Field label="Thumbnail URL (optional)">
                  <input
                    className="input"
                    type="url"
                    value={creativeForm.thumbnailUrl}
                    onChange={(e) =>
                      setCreativeForm({
                        ...creativeForm,
                        thumbnailUrl: e.target.value,
                      })
                    }
                  />
                </Field>
              </div>
              <button type="submit" className="btn btn-primary">
                Add creative
              </button>
            </form>
          )}

          {campaign.creatives.length === 0 ? (
            <p className="card text-muted">
              No creatives yet. Add one to start serving this campaign.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {campaign.creatives.map((c) => (
                <div key={c.id} className="card">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-muted">
                        {c.type}
                      </div>
                      <div className="mt-0.5 text-sm text-text">
                        {c.durationSeconds ?? '?'}s
                      </div>
                    </div>
                    <button
                      onClick={() => removeCreative(c.id)}
                      className="btn btn-ghost text-xs text-red-300"
                    >
                      Delete
                    </button>
                  </div>
                  {c.thumbnailUrl && (
                    <img
                      src={c.thumbnailUrl}
                      alt=""
                      className="mb-3 max-h-32 rounded border border-border object-cover"
                    />
                  )}
                  <div className="break-all font-mono text-[11px] text-muted">
                    {c.mediaUrl}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds - m * 60);
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m - h * 60}m`;
}

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="card">
      <div className="text-xs uppercase tracking-wider text-muted">{label}</div>
      <div className="mt-1 font-mono text-3xl font-bold">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-muted">{sub}</div>}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs uppercase tracking-wider text-muted">
        {label}
      </span>
      {children}
    </label>
  );
}
